'use client';
import {
  Button,
  ContextUserType,
  IMessage,
  IMessagesQueryParams,
  MessageBlocked,
  PostItemReactions,
  PostItemReactionsProps,
  ReplyList,
  Separator,
} from '@ui/components';
import { PostItemAuthor } from '../posts/post-item-author';
import { PostItemsMoreOptions } from '../posts/post-item-more-option';
import { MessageItemDescription } from '@ui/components/organisms/message/components/message-list/message-item-description';
import { MessageItemFiles } from '@ui/components/organisms/message/components/message-list/message-item-files';
import { CircleMinus, CirclePlus } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useReplyRefContext } from '@ui/contexts';
import { ItemUpdateMessageContentWrapper } from '../common/item-update-message-content-wrapper';
import { useMessageUpdateFormContext } from '@ui/contexts/message-update-form-context';
import { useInteractionGuard } from '@hooks/index';

interface Props {
  comment: IMessage;
  userType: ContextUserType;
  creator_id: string;
  user_id: string;
  enableReply: boolean;
  isPinned?: boolean;
  query: IMessagesQueryParams;
  reportLabel?: string;
  isReply?: boolean;
  main_post_ref?: string;
  isDynamicRoute?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  isAuthenticated: boolean;
  isFanOfCreator: boolean;
  parent_comment_thread_id?: string;
  hierarchyType: PostItemReactionsProps['hierarchyType'];
  keepThreadLineOpen?: boolean;
  filter_by_reply_id?: string;
  moderators_ids: string[];
}
export const CommentItem = (props: Props) => {
  const { interceptInteraction } = useInteractionGuard();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const id = useId();
  const { setTargetMessage } = useMessageUpdateFormContext();
  const { setTargetRef, setMessage, expandedList, setExpanded, setReplyUIVersion } =
    useReplyRefContext();
  const replyRef = useRef(null);
  const updateRef = useRef(null);

  const hadChildren = useMemo(() => {
    if (!props.comment?.child_messages) return false;
    return props.comment?.child_messages > 0;
  }, [props.comment?.child_messages]);

  const onTriggerReplyForm = interceptInteraction(() => {
    if (replyRef.current) {
      setMessage({
        ...props.comment,
        queryParams: props.query,
        child_thread_id: props.isReply
          ? props.comment.parent_thread_id
          : props.comment.child_thread_id,
        ...(props.isReply && {
          miscellaneous: {
            is_replying_to: props.comment.id,
          },
        }),
      });
      setTargetRef(props.isReply ? null : replyRef.current);
      setReplyUIVersion(props.isReply ? 2 : 1);
    }
  }, 'You are not a fan of this creator. Collect this badge to join.');

  const onUpdateTrigger = () => {
    if (updateRef.current) {
      setTargetMessage({
        ...props.comment,
        isPost: false,
        targetRef: updateRef.current,
        type: props.isReply ? 'reply' : 'comment',
        query: props.query,
      });
    }
  };

  const handleExpand = () => setExpanded(props.comment.id, !expandedList[props.comment.id]);

  useEffect(() => {
    if (props.keepThreadLineOpen && props.filter_by_reply_id) {
      setExpanded(props.comment.id, true);
    }
  }, [props.keepThreadLineOpen, props.filter_by_reply_id]);

  return (
    <ItemUpdateMessageContentWrapper ref={updateRef}>
      <div id={id} className="overflow-hidden">
        <div className="flex w-full justify-between">
          <PostItemAuthor
            post={props.comment}
            postOwnerUserType={props.comment.user_type === 2 ? 'creator' : 'fan'}
            creatorId={props.creator_id}
            userType={props.userType}
            IsPinned={props.isPinned}
            isDynamicRoute={props.isDynamicRoute}
            communityDetails={props.communityDetails || props.comment.community_details}
            moderators_ids={props.moderators_ids || []}
          />
          <PostItemsMoreOptions
            post={props.comment}
            userType={props.userType}
            userId={props.user_id}
            enableContentFlagging={props.comment.user_id !== props.user_id}
            reportLabel={props.reportLabel}
            onUpdateTrigger={onUpdateTrigger}
            dropdownOpen={dropDownOpen}
            setDropdownOpen={setDropDownOpen}
            isReply={props.isReply}
            communityDetails={props.communityDetails || props.comment.community_details}
            moderators_ids={props.moderators_ids || []}
          />
        </div>
        <div className="flex gap-2">
          <div
            onClick={handleExpand}
            className={twMerge(
              'group visible flex w-7 cursor-pointer flex-col items-center justify-between',
              hadChildren ? 'visible' : 'invisible',
            )}>
            <Separator
              orientation="vertical"
              className="group-hover:bg-korDarkGrey flex-auto flex-grow"
            />
            <Button
              onClick={handleExpand}
              type="secondary"
              size="smallIcon"
              className="h-fit w-fit min-w-fit rounded-full p-1 lg:h-fit lg:w-fit lg:min-w-fit">
              {!expandedList[props.comment.id] && (
                <CirclePlus className="text-korDarkGrey h-5 w-5" />
              )}
              {expandedList[props.comment.id] && (
                <CircleMinus className="text-korDarkGrey h-5 w-5" />
              )}
            </Button>
            <div className="pb-2"></div>
          </div>
          <div className="w-full space-y-2">
            <div className="bg-input rounded p-2 space-y-2">
              {props.comment.block && <MessageBlocked userType={props.userType} />}
              {!props.comment.block && (
                <MessageItemDescription
                  message={props.comment}
                  className="max-w-fit rounded-none bg-transparent p-0"
                />
              )}
              {!props.comment.block &&
                props.comment?.files &&
                props.comment?.files?.images?.length > 0 && (
                  <MessageItemFiles files={props.comment.files} className="max-w-full" />
                )}
              {!props.comment.block && (
                <PostItemReactions
                  query={props.query}
                  post={props.comment}
                  userType={props.userType}
                  hierarchyType={props.hierarchyType}
                  onTriggerReplyForm={onTriggerReplyForm}
                  enableReply={props.enableReply}
                  handleExpand={handleExpand}
                />
              )}
            </div>
            <div ref={replyRef}></div>
            {props.enableReply &&
              props.comment.child_thread_id &&
              expandedList[props.comment.id] && (
                <ReplyList
                  {...props}
                  comment={props.comment}
                  parent_comment_thread_id={
                    props.isReply ? props.comment.child_thread_id : undefined
                  }
                  moderators_ids={props.moderators_ids || []}
                />
              )}
          </div>
        </div>
      </div>
    </ItemUpdateMessageContentWrapper>
  );
};
